/*
 * PureEatiz recipes — shared behaviour for the recipe listing + single recipe sections.
 * Loaded (defer) by every pe-recipe-* section; guarded so it only initialises once.
 *
 * - [data-pe-bookmark="<handle>"]   Save / unsave a recipe (localStorage key "pe:saved-recipes")
 * - [data-pe-share]                 navigator.share, falls back to copying the link
 * - [data-pe-servings]              Serving stepper that scales [data-pe-qty] quantities
 * - [data-pe-recipe-sort]           <select> that sorts every [data-pe-recipe-list]
 * - [data-pe-load-more]             Appends the next page of a recipe grid (Section Rendering API)
 */
(() => {
  if (window.PeRecipe) return;

  const STORAGE_KEY = 'pe:saved-recipes';
  const SORT_KEY = 'pe:recipe-sort';

  /* ---------- Bookmarks ---------- */
  const readSaved = () => {
    try {
      const list = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(list) ? list : [];
    } catch (error) {
      return [];
    }
  };
  const writeSaved = (list) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (error) {
      /* storage unavailable (private mode) — state stays in-page only */
    }
  };
  const syncBookmarks = (root = document) => {
    const saved = new Set(readSaved());
    root.querySelectorAll('[data-pe-bookmark]').forEach((button) => {
      button.setAttribute('aria-pressed', saved.has(button.dataset.peBookmark) ? 'true' : 'false');
    });
  };
  const toggleBookmark = (button) => {
    const id = button.dataset.peBookmark;
    if (!id) return;
    const saved = readSaved();
    const index = saved.indexOf(id);
    if (index === -1) saved.push(id);
    else saved.splice(index, 1);
    writeSaved(saved);
    syncBookmarks();
    const status = button.closest('[data-pe-recipe-root]')?.querySelector('[data-pe-live]');
    if (status) {
      status.textContent = index === -1 ? button.dataset.savedLabel || 'Recipe saved' : button.dataset.removedLabel || 'Recipe removed';
    }
  };

  /* ---------- Share ---------- */
  const share = async (button) => {
    const url = button.dataset.url || window.location.href;
    const title = button.dataset.title || document.title;
    const status = button.closest('[data-pe-recipe-root]')?.querySelector('[data-pe-live]');
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
      } catch (error) {
        /* user cancelled */
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      button.dataset.state = 'copied';
      if (status) status.textContent = button.dataset.copiedLabel || 'Link copied';
      window.setTimeout(() => {
        delete button.dataset.state;
      }, 2200);
    } catch (error) {
      window.prompt(button.dataset.copyPrompt || 'Copy this link', url);
    }
  };

  /* ---------- Serving stepper ---------- */
  const FRACTIONS = { '¼': 0.25, '½': 0.5, '¾': 0.75, '⅓': 1 / 3, '⅔': 2 / 3, '⅛': 0.125 };
  const QTY_PATTERN = /^\s*(\d+\s+\d+\/\d+|\d+\/\d+|\d*[.,]?\d+\s*[¼½¾⅓⅔⅛]?|[¼½¾⅓⅔⅛])/;

  const parseAmount = (text) => {
    let value = 0;
    text
      .trim()
      .split(/\s+/)
      .forEach((part) => {
        const glyph = part.match(/[¼½¾⅓⅔⅛]/);
        if (glyph) {
          value += FRACTIONS[glyph[0]];
          part = part.replace(glyph[0], '');
        }
        if (!part) return;
        if (part.includes('/')) {
          const [a, b] = part.split('/').map(Number);
          if (b) value += a / b;
        } else {
          value += Number(part.replace(',', '.')) || 0;
        }
      });
    return value;
  };

  const formatAmount = (value) => {
    const whole = Math.floor(value + 1e-9);
    const fraction = value - whole;
    const glyphs = [
      [0.25, '¼'],
      [1 / 3, '⅓'],
      [0.5, '½'],
      [2 / 3, '⅔'],
      [0.75, '¾'],
    ];
    if (fraction < 0.02) return String(whole);
    for (const [amount, glyph] of glyphs) {
      if (Math.abs(fraction - amount) < 0.02) return whole ? `${whole}${glyph}` : glyph;
    }
    return String(Math.round(value * 100) / 100);
  };

  const scaleQuantity = (original, factor) => {
    const match = original.match(QTY_PATTERN);
    if (!match || factor === 1) return original;
    const amount = parseAmount(match[1]);
    if (!amount) return original;
    const leading = match[0].match(/^\s*/)[0];
    return leading + formatAmount(amount * factor) + original.slice(match[0].length);
  };

  const updateServings = (root, next) => {
    const base = Number(root.dataset.base) || 1;
    const min = Number(root.dataset.min) || 1;
    const max = Number(root.dataset.max) || 12;
    const count = Math.min(max, Math.max(min, next));
    root.dataset.count = String(count);
    const factor = count / base;
    const label = root.querySelector('[data-pe-servings-label]');
    if (label) {
      const word = count === 1 ? label.dataset.singular : label.dataset.plural;
      label.textContent = `${count} ${word}`;
    }
    root.querySelector('[data-pe-servings-step="-1"]')?.toggleAttribute('disabled', count <= min);
    root.querySelector('[data-pe-servings-step="1"]')?.toggleAttribute('disabled', count >= max);
    const scope = root.closest('[data-pe-recipe-root]') || document;
    scope.querySelectorAll('[data-pe-qty]').forEach((element) => {
      const original = element.dataset.peQty;
      element.textContent = scaleQuantity(original, factor);
    });
  };

  /* ---------- Sorting ---------- */
  const minutes = (element) => {
    const text = (element.dataset.time || '').toLowerCase();
    const value = parseFloat(text);
    if (Number.isNaN(value)) return 9999;
    return /h(ou)?r/.test(text) ? value * 60 : /sec/.test(text) ? value / 60 : value;
  };

  const sortLists = (mode) => {
    document.querySelectorAll('[data-pe-recipe-list]').forEach((list) => {
      const items = Array.from(list.children);
      if (!items.length) return;
      if (!list.dataset.peOriginalOrder) {
        items.forEach((item, index) => {
          item.dataset.peIndex = item.dataset.peIndex || String(index);
        });
        list.dataset.peOriginalOrder = 'true';
      }
      const card = (item) => item.querySelector('[data-pe-recipe]') || item;
      const compare = {
        newest: (a, b) => Number(a.dataset.peIndex) - Number(b.dataset.peIndex),
        oldest: (a, b) => Number(card(a).dataset.date) - Number(card(b).dataset.date) || Number(b.dataset.peIndex) - Number(a.dataset.peIndex),
        title: (a, b) => (card(a).dataset.title || '').localeCompare(card(b).dataset.title || ''),
        quickest: (a, b) => minutes(card(a)) - minutes(card(b)),
      }[mode] || null;
      if (!compare) return;
      items.sort(compare).forEach((item) => list.appendChild(item));
    });
  };
  const currentSort = () => {
    try {
      return window.sessionStorage.getItem(SORT_KEY) || 'newest';
    } catch (error) {
      return 'newest';
    }
  };

  /* ---------- Load more ---------- */
  const loadMore = async (link) => {
    const list = document.getElementById(link.dataset.target);
    if (!list || link.getAttribute('aria-busy') === 'true') return;
    const url = new URL(link.href, window.location.origin);
    url.searchParams.set('section_id', link.dataset.sectionId);
    link.setAttribute('aria-busy', 'true');
    link.classList.add('is-loading');
    try {
      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(String(response.status));
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const incoming = doc.getElementById(link.dataset.target);
      if (!incoming) throw new Error('Missing list');
      const offset = list.children.length;
      const newItems = Array.from(incoming.children);
      newItems.forEach((item, index) => {
        item.dataset.peIndex = String(offset + index);
        list.appendChild(item);
      });
      syncBookmarks(list);
      if (currentSort() !== 'newest') sortLists(currentSort());
      newItems[0]?.querySelector('a[href]:not([tabindex="-1"])')?.focus({ preventScroll: true });

      const nextLink = doc.querySelector(`[data-pe-load-more][data-target="${link.dataset.target}"]`);
      if (nextLink) {
        link.href = nextLink.getAttribute('href');
      } else {
        link.closest('[data-pe-load-more-wrap]')?.setAttribute('hidden', '');
      }
      const liveRegion = link.closest('[data-pe-recipe-root]')?.querySelector('[data-pe-live]');
      if (liveRegion) liveRegion.textContent = `${newItems.length} ${link.dataset.loadedLabel || 'more recipes loaded'}`;
    } catch (error) {
      window.location.href = link.href;
    } finally {
      link.removeAttribute('aria-busy');
      link.classList.remove('is-loading');
    }
  };

  /* ---------- Events ---------- */
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const bookmark = target.closest('[data-pe-bookmark]');
    if (bookmark) {
      event.preventDefault();
      toggleBookmark(bookmark);
      return;
    }
    const shareButton = target.closest('[data-pe-share]');
    if (shareButton) {
      event.preventDefault();
      share(shareButton);
      return;
    }
    const stepper = target.closest('[data-pe-servings-step]');
    if (stepper) {
      const root = stepper.closest('[data-pe-servings]');
      if (root) updateServings(root, Number(root.dataset.count || root.dataset.base || 1) + Number(stepper.dataset.peServingsStep));
      return;
    }
    const more = target.closest('[data-pe-load-more]');
    if (more && !event.metaKey && !event.ctrlKey && !event.shiftKey) {
      event.preventDefault();
      loadMore(more);
    }
  });

  document.addEventListener('change', (event) => {
    const select = event.target instanceof HTMLSelectElement ? event.target : null;
    if (!select || !select.matches('[data-pe-recipe-sort]')) return;
    try {
      window.sessionStorage.setItem(SORT_KEY, select.value);
    } catch (error) {
      /* ignore */
    }
    document.querySelectorAll('[data-pe-recipe-sort]').forEach((other) => {
      if (other !== select) other.value = select.value;
    });
    sortLists(select.value);
    const status = select.closest('[data-pe-recipe-root]')?.querySelector('[data-pe-live]');
    if (status) status.textContent = `${select.dataset.sortedLabel || 'Recipes sorted by'} ${select.options[select.selectedIndex].text}`;
  });

  const init = (root = document) => {
    syncBookmarks(root);
    const mode = currentSort();
    root.querySelectorAll('[data-pe-recipe-sort]').forEach((select) => {
      if (select.querySelector(`option[value="${mode}"]`)) select.value = mode;
    });
    if (mode !== 'newest') sortLists(mode);
    root.querySelectorAll('[data-pe-servings]').forEach((stepper) => {
      updateServings(stepper, Number(stepper.dataset.base) || 1);
    });
  };

  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));

  window.PeRecipe = { syncBookmarks, sortLists, init };
})();
